'use client'

import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Switch } from '@/components/ui/switch'
import { loadSofaElement } from '@/configurator/DynamicLoadSofaShape'
import {
  getDefaultDimensionOverrides,
  getShapeChangeableProperties,
  type ChangeableProperties,
} from '@/configurator/utils/shapeConfigUtils'
import {
  FAMILY_ANCHOR_TYPES,
  isStraightBlueprintType,
} from '@/configurator/blueprint-overlay/anchors'
import SofaDimensionsBlueprintField from './components/SofaDimensionsBlueprintField'

/**
 * Hidden admin page (/products/sofa-blueprint-preview): renders a spread of
 * module types with the on-blueprint dimension editor, so the label
 * semantics for corner/chaise families can be decided before per-family
 * anchors are written. Edits stay local to each card — nothing is saved.
 */
const PREVIEW_TYPES = [
  'A2L',
  'AUFULLMANYPILLOWSQ',
  'AUFULLSQ',
  'A1SQL',
  'ESQ',
  'EMANYPILLOWSQ',
  'LCHOUTERMANYPILLOWSL',
  'LCHOUTERMANYPILLOWSR',
  'LCHL',
  'LCHR',
  'CORNERL',
  'CORNERR',
  'CORNERCUTL',
  'ANGLECORNER',
  'ANGLECORNERFLAT',
  'ANGLECORNERLCHL',
  'ANGLECORNERLCHR',
  'ARCHCORNERL',
  'OTKCMANYPILLOWL',
  'OTKCMANYPILLOWR',
  'OTTP1L',
  'OTTP1R',
  'LCHOUTERL',
  'SCHEZLONG',
  'SLEEPL',
  'COMPOSITE',
  'ROUND',
  'PUF',
]

function PreviewCard({ type, debug }: { type: string; debug: boolean }) {
  const form = useForm({
    defaultValues: {
      type,
      dimensions: {
        width: 200 as number | null,
        length: 100 as number | null,
        backrest_width: null as number | null,
        armrest_width: null as number | null,
        extendable_part_length: null as number | null,
        corner_radius: null as number | null,
        corner_part_length: null as number | null,
        angle: null as number | null,
        composition: null,
      },
    },
  })
  const [changeable, setChangeable] = useState<ChangeableProperties>({})
  const [propertiesType, setPropertiesType] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const mod = await loadSofaElement(type)
      const settingsDims = mod?.getDefaultSettings?.().dimensions
      const defaults = (
        settingsDims
          ? { ...settingsDims, ...getDefaultDimensionOverrides(type) }
          : undefined
      ) as Record<string, number | null | undefined> | undefined
      const props = await getShapeChangeableProperties(type)
      if (cancelled) return
      if (defaults) {
        const current = form.getValues('dimensions')
        form.reset({
          type,
          dimensions: {
            ...current,
            width: defaults.width ?? current.width,
            length: defaults.length ?? current.length,
            backrest_width: defaults.backrest_width ?? null,
            armrest_width: defaults.armrest_width ?? null,
            extendable_part_length: defaults.extendable_part_length ?? null,
            corner_radius: defaults.corner_radius ?? null,
            corner_part_length: defaults.corner_part_length ?? null,
            angle: defaults.angle ?? null,
          },
        })
      }
      setChangeable(props)
      setPropertiesType(type)
    })()
    return () => {
      cancelled = true
    }
  }, [type, form])

  const flags = (
    [
      'width',
      'length',
      'backrest_width',
      'armrest_width',
      'extendable_part_length',
      'corner_part_length',
      'corner_radius',
      'angle',
    ] as const
  )
    .filter(k => changeable[k])
    .join(', ')

  return (
    <div className="rounded-lg border bg-card p-3 space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-mono text-sm font-semibold">{type}</h3>
        <span className="text-[11px] text-muted-foreground">
          {isStraightBlueprintType(type)
            ? 'straight (default anchors)'
            : FAMILY_ANCHOR_TYPES.includes(type)
              ? 'family anchors'
              : 'needs family anchors'}
        </span>
      </div>
      <FormProvider {...form}>
        <SofaDimensionsBlueprintField
          id={0}
          height={320}
          changeableProperties={changeable}
          propertiesType={propertiesType}
          debug={debug}
        />
      </FormProvider>
      <p className="text-[11px] text-muted-foreground">
        Editable: {flags || '—'}
      </p>
    </div>
  )
}

export default function SofaBlueprintPreview() {
  const [debug, setDebug] = useState(true)
  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold">Blueprint dimension preview</h1>
          <p className="text-sm text-muted-foreground">
            Bounding-box anchors (Width top, Length left) on every type;
            backrest/armrest drawn for straight types (armrest on the top edge),
            backrest/corner part per family (CORNER*, OTTP1*, OTKCMANYPILLOW*,
            ANGLECORNERLCH*, ANGLECORNERFLAT), extension chained under Length;
            anything not placeable is shown under the drawing. Edits here are
            not saved.
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-2">
            <Switch checked={debug} onCheckedChange={setDebug} />
            Outline measured rect
          </label>
          <label
            className="flex items-center gap-2 text-muted-foreground"
            title="Per-family anchor overrides are not defined yet (Phase 4)"
          >
            <Switch checked={false} disabled />
            Override anchors (none yet)
          </label>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {PREVIEW_TYPES.map(type => (
          <PreviewCard key={type} type={type} debug={debug} />
        ))}
      </div>
    </div>
  )
}
